import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, FolderOpen, Video, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoriesTab = ({ categories, onRefresh }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/categories`, {
        name: newCategory.name.trim(),
        description: newCategory.description.trim() || undefined
      });
      
      toast.success('Category added successfully!');
      setNewCategory({ name: '', description: '' });
      setShowAddDialog(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-brand font-bold text-white">Category Management</h2>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          
          <DialogContent className="glass-dark border-white/20 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Category</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name" className="text-white">Category Name</Label>
                <Input
                  id="category-name"
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter category name"
                  className="form-input text-white"
                  required
                  data-testid="category-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category-description" className="text-white">Description (Optional)</Label>
                <Textarea
                  id="category-description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter category description"
                  className="form-input text-white min-h-[100px]"
                  data-testid="category-description-input"
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white"
                  data-testid="submit-category"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add Category'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 ? (
          <div className="col-span-full">
            <Card className="glass-dark border-white/20">
              <CardContent className="p-12 text-center">
                <div className="text-6xl mb-4">📁</div>
                <h3 className="text-xl font-semibold mb-2 text-white">No Categories Yet</h3>
                <p className="text-white/70 mb-4">Create categories to organize your videos.</p>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Category
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          categories.map((category) => (
            <CategoryCard key={category.id} category={category} onDelete={() => handleDeleteCategory(category.id)} />
          ))
        )}
      </div>
    </div>
  );
};

const CategoryCard = ({ category, onDelete }) => {
  return (
    <Card className="glass-dark border-white/20 card-hover">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
              <FolderOpen className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-white text-lg">{category.name}</CardTitle>
          </div>
          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
            <Video className="h-3 w-3 mr-1" />
            {category.video_count}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {category.description && (
          <p className="text-white/70 text-sm mb-4">
            {category.description}
          </p>
        )}
        
        <div className="flex items-center justify-between text-xs text-white/50">
          <div>
            <div>Videos: {category.video_count}</div>
            <div>Created: {new Date(category.created_at).toLocaleDateString()}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid={`delete-category-${category.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoriesTab;